/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { FormInput, Loader } from '@omnigenbiodata/ui';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useFormik } from 'formik';
import ConfirmationModal from './components/ConfirmationModal';
import { CSVLink } from 'react-csv';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { DATE_VALUE_FORMAT, DATE_FORMAT } from '../../../../../../core/constants/forms.constants';
import InnerLayout from '../../../../../../layout/Inner';
import { CreateStagedShipmentInput } from '../../../../../../core/api/lab.types';
import {
  getShipmentIsBusySelector,
  getShipmentResponseSelector,
  getShipmentThunk,
} from '../../../../../../store/shipmentGet';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { getShipmentManifestThunk } from '../../../../../../store/shipmentGetManifest/thunks';
import {
  getShipmentManifestIsBusySelector,
  getShipmentManifestResponseSelector,
  manifestDownloadReset,
} from '../../../../../../store/shipmentGetManifest';
import { SHIPMENT_STATUS_COLOUR } from '../../../../../../core/constants/lab.constants';
import { finaliseShipmentIsBusySelector } from '../../../../../../store/shipmentFinalise/selectors';
import { RiBarcodeLine } from 'react-icons/ri';
import { resetManifestStagedShipment } from '../../../../../../store/stagedShipmentManifest';
// import { useNavigate } from 'react-router-dom';
// import ROUTES from '../../../../../../core/constants/routes.constants';

function ViewShipmentScene() {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();

  const shipmentResponse = useAppSelector(getShipmentResponseSelector);
  const manifest = useAppSelector(getShipmentManifestResponseSelector);
  const shipmentIsBusy = useAppSelector(getShipmentIsBusySelector);
  const manifestIsBusy = useAppSelector(getShipmentManifestIsBusySelector);
  const finaliseShipmentIsBusy = useAppSelector(finaliseShipmentIsBusySelector);
  const manifestDownload = useRef<any>(null);
  const [showConfirm, setConfirm] = useState(false);
  const { shippingId } = useParams<any>();
  const [shipmentStatus, setShipmentStatus] = useState<string>('STAGED');
  const [shipmentData, setShipmentData] = useState<CreateStagedShipmentInput>({
    destination: '',
    shipmentType: '',
    plannedShipmentDate: '',
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      shippingDate: moment().format(DATE_VALUE_FORMAT),
    },
    onSubmit: () => {},
  });

  const finaliseShipment = () => {
    setConfirm(true);
  };

  useEffect(() => {
    if (shippingId) {
      dispatch(getShipmentThunk(shippingId));
    }
  }, [dispatch, shippingId]);

  const handleClose = () => {
    setConfirm(false);
    if (shippingId) {
      dispatch(getShipmentThunk(shippingId));
    }
  };

  useEffect(() => {
    if (shipmentResponse?.shipmentId === shippingId) {
      setShipmentData({
        destination: shipmentResponse?.destination || '',
        shipmentType: shipmentResponse?.shipmentType || '',
        plannedShipmentDate:
          shipmentResponse?.status === 'MANIFESTED'
            ? shipmentResponse?.plannedShipmentDate
            : shipmentResponse?.shippedDate || '',
      });

      setShipmentStatus(shipmentResponse?.status.toUpperCase() || 'STAGED');
    }
  }, [shipmentResponse]);

  useEffect(() => {
    if (shipmentResponse?.plannedShipmentDate) {
      formik.setFieldValue('shippingDate', moment(shipmentData?.plannedShipmentDate).format(DATE_VALUE_FORMAT));
    }
  }, [shipmentData]);

  useEffect(() => {
    if (manifest && manifest.link) {
      window.open(manifest.link, '_blank');
      dispatch(resetManifestStagedShipment());
    }
  }, [manifest]);

  const handleDownloadManifest = () => {
    if (shipmentResponse?.shipmentId) {
      dispatch(getShipmentManifestThunk(shipmentResponse?.shipmentId));
    }
  };

  useEffect(() => {
    return () => {
      dispatch(manifestDownloadReset());
    };
  }, [dispatch]);

  return (
    <InnerLayout>
      <>
        {shippingId && (
          <CSVLink
            filename="DMUK Draft Manifest.csv"
            data={manifest?.link || ''}
            target="_blank"
            data-testid="draft-manifest-download"
            ref={manifestDownload}
          />
        )}
        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              Shipment Summary
            </Typography>

            <Button
              variant="outlined"
              data-testid="btnDdownloadManifest"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={handleDownloadManifest}
              sx={{
                textTransform: 'uppercase',
                fontWeight: 700,
                px: 3,
                py: 1,
              }}
            >
              Download Manifest
            </Button>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                Shipment Status
              </Typography>

              <Chip
                label={shipmentStatus}
                sx={{
                  bgcolor: `${SHIPMENT_STATUS_COLOUR[shipmentStatus]}`,
                  color: '#fff',
                  fontWeight: 700,
                  minWidth: 100,
                  height: 30,
                  borderRadius: 20,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                Shipment Destination
              </Typography>

              <Typography fontWeight={600}>{shipmentData.destination}</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                Shipment Type
              </Typography>

              <Typography fontWeight={600}>{shipmentData.shipmentType}</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" fontWeight={700} color="text.secondary" gutterBottom>
                Shipment Date
              </Typography>
              {shipmentResponse?.status === 'MANIFESTED' && (
                <Box mt={-2}>
                  <FormInput
                    error={formik.errors.shippingDate}
                    showErrorMessage={false}
                    touched={formik.touched.shippingDate}
                    name={`shippingDate`}
                    label=""
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    type="date"
                    value={formik.values.shippingDate}
                    inputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>
              )}
              {shipmentResponse?.status === 'SHIPPED' && (
                <Typography fontWeight={600}>{moment(shipmentData.plannedShipmentDate).format(DATE_FORMAT)}</Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Paper
          elevation={3}
          sx={{
            p: 4,
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'divider',
            mt: 2,
          }}
        >
          <Grid container justifyContent="space-between" alignItems="flex-start" spacing={2} mb={3}>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" fontWeight={700}>
                {shipmentStatus === 'MANIFESTED' && `Manifested boxes`}
                {shipmentStatus === 'SHIPPED' && `Shipped boxes`}
              </Typography>

              <Stack direction="row" spacing={4} mt={1}>
                <Typography variant="body1">
                  Total Eligible Samples:{' '}
                  <Box component="span" sx={{ color: '#16A34A', fontWeight: 700 }}>
                    {shipmentResponse?.totalAliquotCount ?? 0}
                  </Box>
                </Typography>

                <Typography variant="body1">
                  Total Boxes:{' '}
                  <Box component="span" sx={{ color: '#1976d2', fontWeight: 700 }}>
                    {shipmentResponse?.cryoboxes?.length ?? 0}
                  </Box>
                </Typography>
                <Typography variant="body1">
                  Manifested Date:{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {moment(shipmentResponse?.manifestDate).format(DATE_FORMAT)}
                  </Box>
                </Typography>
              </Stack>
            </Grid>

            {shipmentStatus === 'MANIFESTED' && (
              <Grid item>
                <Button
                  data-testid="btnFinaliseShipment"
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 1,
                    fontWeight: 700,
                  }}
                  onClick={() => finaliseShipment()}
                >
                  FINALISE SHIPMENT
                </Button>
              </Grid>
            )}
          </Grid>

          <Box sx={{ width: '100%' }}>
            <TableContainer
              component={Box}
              sx={{
                mt: 2,
              }}
            >
              <Table
                sx={{
                  borderCollapse: 'separate',
                  borderSpacing: '0 12px',
                }}
              >
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#F5F5F5',
                      '& th': {
                        border: 0,
                        fontWeight: 700,
                        fontSize: 15,
                        color: '#374151',
                        py: 1.8,
                      },
                    }}
                  >
                    <TableCell>Box Serial No</TableCell>
                    <TableCell align="center">Final Freezer</TableCell>
                    <TableCell align="center">Final Location</TableCell>
                    <TableCell align="center">Sample Type</TableCell>
                    <TableCell align="center">Samples</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(shipmentResponse?.cryoboxes || []).map((box) => {
                    return (
                      <TableRow
                        key={box.cryoboxUuid}
                        sx={{
                          '& td': {
                            backgroundColor: '#FFFFFF',
                            borderTop: '1px solid #E5E7EB',
                            borderBottom: '1px solid #E5E7EB',
                            py: 1.2,
                          },
                          '& td:first-of-type': {
                            borderLeft: '1px solid #E5E7EB',
                            borderTopLeftRadius: 5,
                            borderBottomLeftRadius: 5,
                          },
                          '& td:last-of-type': {
                            borderRight: '1px solid #E5E7EB',
                            borderTopRightRadius: 5,
                            borderBottomRightRadius: 5,
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          {box.serialNo}
                          {box.cryoboxBarcode !== '' && (
                            <>
                              <br />
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  py: 0.5,
                                  fontWeight: 'normal',
                                }}
                              >
                                <RiBarcodeLine fontSize={15} />
                                {box.cryoboxBarcode === null ? '-------------' : box.cryoboxBarcode}
                              </Box>
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center">{box.freezer.name}</TableCell>
                        <TableCell align="center" color="success.main">
                          Shelf {box.freezerShelfNo} / Rack {box.shelfRackNo} / Pos {box.rackPositionNo}
                        </TableCell>
                        <TableCell align="center">{box.sampleType}</TableCell>
                        <TableCell align="center">{box.aliquotCount}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>

        <ConfirmationModal
          isOpen={showConfirm}
          onClose={handleClose}
          staged={shipmentResponse}
          t={t}
          shipmentDate={formik.values.shippingDate}
        />

        <Loader isVisible={shipmentIsBusy} label={t('loaders.shipment')} />
        <Loader isVisible={manifestIsBusy} label={t('loaders.manifest')} />
        <Loader isVisible={finaliseShipmentIsBusy} label={t('loaders.shipping')} />
      </>
    </InnerLayout>
  );
}

export default ViewShipmentScene;
